import React, { useMemo, useState } from 'react';
import { Button, Form, InputNumber, Modal, Select, Space, Typography, message } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import {
  OPS_TOOL_EXECUTE_SETTLEMENT_OPS,
  OPS_TOOL_SETTLEMENT_FEE_ITEMS,
} from 'pages/ops-tool/settlement/graphql/opsToolSettlementGraphql';
import { usePricingScenarios } from 'graphql/queries/settlement/getSettlementCommonQueries';
import { hasResource } from '../../../../utils/resourceAuth';
import { ResourceCode } from '../../../../constants/resourceCodes';

const { Text } = Typography;

const ADD_INVOICE_LINE_ITEM_FEE_ITEM_MAP = 'ADD_INVOICE_LINE_ITEM_FEE_ITEM_MAP';
const DELETE_INVOICE_LINE_ITEM_FEE_ITEM_MAP = 'DELETE_INVOICE_LINE_ITEM_FEE_ITEM_MAP';

type AddFormValues = {
  businessScenarioCode: number;
  invoiceLineItemId: number;
  settlementFeeItemIds: number[];
};

const InvoiceLineItemFeeItemMapTab: React.FC = () => {
  const canOperate = hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]);

  const [addForm] = Form.useForm<AddFormValues>();
  const [addFormOpen, setAddFormOpen] = useState(false);
  const [addConfirmOpen, setAddConfirmOpen] = useState(false);
  const [addValues, setAddValues] = useState<AddFormValues | null>(null);
  const [businessScenarioCode, setBusinessScenarioCode] = useState<number>();

  const [deleteForm] = Form.useForm<{ id: number }>();
  const [deleteFormOpen, setDeleteFormOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const { scenarios, loading: scenariosLoading } = usePricingScenarios();
  const businessScenarioOptions = useMemo(
    () => scenarios.map(s => ({ value: Number(s.businessScenarioCode), label: s.businessScenarioName })),
    [scenarios]
  );

  const { data: feeItemsData, loading: feeItemsLoading } = useQuery(OPS_TOOL_SETTLEMENT_FEE_ITEMS, {
    skip: !businessScenarioCode,
    variables: { businessScenarioCode: businessScenarioCode! },
  });

  const feeItemOptions = (feeItemsData?.opsToolSettlementFeeItems ?? []).map(
    (item: { id: string; code: number; name: string }) => ({
      value: Number(item.id),
      label: `${item.name} (${item.code})`,
    })
  );

  const [executeOps, { loading: submitting }] = useMutation(OPS_TOOL_EXECUTE_SETTLEMENT_OPS);

  const openAddForm = () => {
    addForm.resetFields();
    setBusinessScenarioCode(undefined);
    setAddFormOpen(true);
  };

  const handleAddFormOk = async () => {
    try {
      const values = await addForm.validateFields();
      setAddValues(values);
      setAddFormOpen(false);
      setAddConfirmOpen(true);
    } catch {
      // 校验未通过，Form 已在对应字段展示错误提示，这里不需要额外处理
    }
  };

  const handleAddConfirm = async () => {
    if (!addValues) return;
    try {
      const result = await executeOps({
        variables: {
          input: {
            type: ADD_INVOICE_LINE_ITEM_FEE_ITEM_MAP,
            paramsJson: JSON.stringify({
              invoiceLineItemId: addValues.invoiceLineItemId,
              settlementFeeItemIds: addValues.settlementFeeItemIds,
            }),
          },
        },
      });
      const payload = result.data?.opsToolExecuteSettlementOps;
      if (!payload?.success) {
        message.error(payload?.message ?? '新增失败');
        return;
      }
      message.success(payload.message ?? '新增成功');
      setAddConfirmOpen(false);
      setAddValues(null);
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  const openDeleteForm = () => {
    deleteForm.resetFields();
    setDeleteFormOpen(true);
  };

  const handleDeleteFormOk = async () => {
    try {
      const values = await deleteForm.validateFields();
      setDeleteId(values.id);
      setDeleteFormOpen(false);
      setDeleteConfirmOpen(true);
    } catch {
      // 校验未通过，Form 已在对应字段展示错误提示，这里不需要额外处理
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteId == null) return;
    try {
      const result = await executeOps({
        variables: {
          input: {
            type: DELETE_INVOICE_LINE_ITEM_FEE_ITEM_MAP,
            paramsJson: JSON.stringify({ id: deleteId }),
          },
        },
      });
      const payload = result.data?.opsToolExecuteSettlementOps;
      if (!payload?.success) {
        message.error(payload?.message ?? '删除失败');
        return;
      }
      message.success(payload.message ?? '删除成功');
      setDeleteConfirmOpen(false);
      setDeleteId(null);
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  return (
    <>
      <Space>
        {canOperate && (
          <Button type="primary" onClick={openAddForm}>
            添加
          </Button>
        )}
        {canOperate && (
          <Button danger onClick={openDeleteForm}>
            删除
          </Button>
        )}
      </Space>

      <Modal
        title="新增发票行项目费用项关联"
        open={addFormOpen}
        onOk={handleAddFormOk}
        onCancel={() => setAddFormOpen(false)}
        okText="下一步"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={addForm} layout="vertical">
          <Form.Item
            name="businessScenarioCode"
            label="业务场景（仅用于筛选费用项候选，不作为提交参数）"
            rules={[{ required: true, message: '请选择业务场景' }]}
          >
            <Select
              options={businessScenarioOptions}
              loading={scenariosLoading}
              onChange={value => {
                setBusinessScenarioCode(value);
                addForm.setFieldValue('settlementFeeItemIds', []);
              }}
            />
          </Form.Item>
          <Form.Item
            name="invoiceLineItemId"
            label="发票行项目ID"
            rules={[{ required: true, message: '请输入发票行项目ID' }]}
          >
            <InputNumber style={{ width: '100%' }} min={1} precision={0} />
          </Form.Item>
          <Form.Item
            name="settlementFeeItemIds"
            label="费用项"
            rules={[{ required: true, message: '请选择至少一个费用项' }]}
          >
            <Select
              mode="multiple"
              loading={feeItemsLoading}
              options={feeItemOptions}
              placeholder="请先选择业务场景"
              disabled={!businessScenarioCode}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="确认新增"
        open={addConfirmOpen}
        onOk={handleAddConfirm}
        onCancel={() => setAddConfirmOpen(false)}
        confirmLoading={submitting}
        okText="确认新增"
        cancelText="取消"
      >
        <Text>
          确认新增：发票行项目ID = {addValues?.invoiceLineItemId}，费用项ID = [
          {addValues?.settlementFeeItemIds?.join(', ')}]？
        </Text>
      </Modal>

      <Modal
        title="删除发票行项目费用项关联"
        open={deleteFormOpen}
        onOk={handleDeleteFormOk}
        onCancel={() => setDeleteFormOpen(false)}
        okText="下一步"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={deleteForm} layout="vertical">
          <Form.Item
            name="id"
            label="发票行项目-结算费用项关联ID"
            rules={[{ required: true, message: '请输入关联ID' }]}
          >
            <InputNumber style={{ width: '100%' }} min={1} precision={0} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="确认删除"
        open={deleteConfirmOpen}
        onOk={handleDeleteConfirm}
        onCancel={() => setDeleteConfirmOpen(false)}
        confirmLoading={submitting}
        okText="确认删除"
        okButtonProps={{ danger: true }}
        cancelText="取消"
      >
        <Text>确认删除关联ID = {deleteId} 吗？该操作不可撤销。</Text>
      </Modal>
    </>
  );
};

export default InvoiceLineItemFeeItemMapTab;
